import React, { useEffect, useState } from "react";
import API from "../api";
import "../Style/PadingList.css";
import { downloadPdfBlob } from "../utilities/downloadPdf";
import { COURSE_PLAN_LABEL, getCoursePlan } from "../utilities/coursePlan";
import CoursePlanLink from "./CoursePlanLink";

const translations = {
    id: "Հերթական համար",
    cart_id: "Զամբյուղի ID",
    first_name: "Անուն",
    last_name: "Ազգանուն",
    patronymic: "Հայրանուն",
    passport_series: "Անձնագրի սերիա",
    htsh: "ՀԾՀ - հանրային ծառայությունների համարանիշը",
    issued_by: "Տրված է կողմից",
    residence: "Բնակության վայր",
    citizenship: "Քաղաքացիություն",
    phone: "Հեռախոսահամար",
    email: "Էլ․ փոստ",
    confirm_email: "Հաստատված Էլ․ փոստ",
    selected_course: "Ընտրված դասընթաց",
    course_plan: COURSE_PLAN_LABEL,
    payment_method: "Վճարման եղանակ",
    other_student: "Ում համար է վճարել (Ուսանողի անուն, ազգանուն)",
    discount: "Հատուկ գնառաջարկ",
    has_discount: "Ունի Հատուկ գնառաջարկ",
    total_price: "Ընդհանուր գին",
    selected_date: "Դասընթացի ավարտի օր",
    status: "Կարգավիճակ",
    pdf_path: "PDF ճանապարհ",
    created_at: "Կնքման ամսաթիվ",
};

const fieldGroups = {
    personalInfo: [
        "first_name",
        "last_name",
        "patronymic",
        "passport_series",
        "htsh",
        "issued_by",
        "residence",
        "citizenship",
    ],
    contactInfo: ["phone", "email", "confirm_email"],
    courseInfo: [
        "selected_course",
        "course_plan",
        "payment_method",
        "selected_date",
        "total_price",
        "discount",
        "has_discount",
        "other_student",
    ],
    systemInfo: [
        "id",
        "cart_id",
        "status",
        "created_at",
    ],
};

const PendingList = () => {
    const [items, setItems] = useState([]);
    const [expandedUser, setExpandedUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [imageSrcs, setImageSrcs] = useState({});
    const [downloadingCard, setDownloadingCard] = useState(null);
    const [downloadingPdf, setDownloadingPdf] = useState(null);

    // Delete is allowed for both managers and admins — the backend enforces
    // requireRole("manager", "admin") on DELETE /users/delete/:id.

    const handleDelete = async (id) => {
        if (
            !window.confirm("Վստա՞հ եք, որ ցանկանում եք ջնջել այս օգտատիրոջ տվյալները։")
        ) {
            return;
        }

        try {
            await API.delete(`/users/delete/${id}`);
            alert("Տվյալները հաջողությամբ ջնջվեցին։");
            setItems((prevItems) => prevItems.filter((item) => item.id !== id));
        } catch (error) {
            console.error("Ջնջման սխալ:", error.message);
            alert("Տեղի ունեցավ սխալ տվյալները ջնջելիս։");
        }
    };

    useEffect(() => {
        const fetchItems = async () => {
            try {
                const response = await API.get("/pending-items");
                setItems(response.data);
            } catch (err) {
                setError("Տվյալները բեռնելու ընթացքում խնդիր առաջացավ։");
            } finally {
                setLoading(false);
            }
        };
        fetchItems();
    }, []);

    // Բեռնում ենք benefit քարտի նկարները user.id-երով
    useEffect(() => {
        const fetchImages = async () => {
            const imageMap = {};
            for (const item of items) {
                if (item.id) {
                    try {
                        const res = await API.get(`/users/${item.id}/image`, {
                            responseType: "blob",
                        });
                        const imageUrl = URL.createObjectURL(res.data);
                        imageMap[item.id] = imageUrl;
                    } catch (err) {
                        console.error(`Չհաջողվեց բեռնել քարտը ${item.id}-ի համար։`);
                    }
                }
            }
            setImageSrcs(imageMap);
        };

        if (items.length > 0) fetchImages();
    }, [items]);

    const fetchBenefitImage = async (id) => {
        if (!id || imageSrcs[id]) return;
        try {
            const res = await API.get(`/users/${id}/image`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            setImageSrcs((prev) => ({ ...prev, [id]: url }));
        } catch (err) {
            console.error("Քարտի նկարը բեռնելիս սխալ:", err);
        }
    };

    const handleExpand = (index, id) => {
        if (expandedUser === index) {
            setExpandedUser(null);
        } else {
            setExpandedUser(index);
            fetchBenefitImage(id);
        }
    };

    const handleAction = async (id, actionType) => {
        const endpoint =
            actionType === "approve"
                ? `/users/approve-user/${id}`
                : `/users/reject-user/${id}`;
        try {
            await API.post(endpoint);
            alert(
                `Օգտատերը ID-${id} ${
                    actionType === "approve" ? "հաստատված" : "մերժված"
                } է։`
            );
            setItems((prevItems) => prevItems.filter((item) => item.id !== id));
        } catch (error) {
            console.error(
                `${actionType === "approve" ? "Հաստատման" : "Մերժման"} սխալ:`,
                error.message
            );
        }
    };

    const handleDownloadPDF = async (userId) => {
        if (!userId) {
            alert("PDF ֆայլը հասանելի չէ։");
            return;
        }

        try {
            setDownloadingPdf(true);

            const response = await API.get(`/users/${userId}/pdf`, { responseType: "blob" });
            downloadPdfBlob(new Blob([response.data]), `agreement-${userId}.pdf`);
        } catch (error) {
            console.error("❌ PDF ներբեռնման սխալ:", error);
            alert(`PDF ներբեռնման սխալ: ${error.message}`);
        } finally {
            setDownloadingPdf(false);
        }
    };

    const renderFieldGroup = (item, group, title) => {
        const fields = Object.entries(item).filter(([key]) =>
            fieldGroups[group]?.includes(key)
        );

        if (fields.length === 0) return null;

        return (
            <div className="info-group">
                <h3 className="info-title">{title}</h3>

                <div className="user-info-section">
                    {fields.map(([key, value]) => {

                        let displayValue = value;

                        if (key === "payment_method") {
                            displayValue =
                                value === "full"
                                    ? "Միանվագ"
                                    : value === "monthly"
                                        ? "Մաս մաս"
                                        : value === "paylater"
                                            ? "Հետվճարային"
                                            : value;
                        }

                        if (key === "course_plan") {
                            displayValue = (
                                <CoursePlanLink href={getCoursePlan(item)} fallback="Չկան տվյալներ">
                                    {COURSE_PLAN_LABEL}
                                </CoursePlanLink>
                            );
                        }

                        return (
                            <div key={key} className="info-field">

                                <div className="info-label">
                                    {translations[key] || key}
                                </div>

                                <div className="info-value">
                                    {displayValue || "Չկան տվյալներ"}
                                </div>

                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };
    return (
        <div className="pading_list">
            <div className="container_pading_list">
                <h1>Կարգավորման ենթակա օգտատերեր</h1>

                {loading ? (
                    <div className="loading-message">Բեռնում...</div>
                ) : error ? (
                    <div className="error-message">{error}</div>
                ) : items.length > 0 ? (
                    <table>
                        <thead>
                        <tr>
                            <th>Անուն</th>
                            <th>Ազգանուն</th>
                            <th>Հեռախոս</th>
                            <th>Գործողություններ</th>
                        </tr>
                        </thead>
                        <tbody>
                        {items.map((item, index) => (
                            <React.Fragment key={item.id}>
                                <tr className={expandedUser === index ? "expanded-row" : ""}>
                                    <td>{item.first_name || "Չկան տվյալներ"}</td>
                                    <td>{item.last_name || "Չկան տվյալներ"}</td>
                                    <td>{item.phone || "Չկան տվյալներ"}</td>
                                    <td>
                                        <button
                                            className="expand-btn icon-button"
                                            onClick={() => handleExpand(index, item.id)}
                                        >
                                            {expandedUser === index ? "Փակել" : "Տեսնել ավելին"}
                                        </button>
                                    </td>
                                </tr>
                                {expandedUser === index && (
                                    <tr className="expanded-row">
                                        <td colSpan="4">
                                            <div className="expanded-content">
                                                {renderFieldGroup(item, "personalInfo", "Անձնական տվյալներ")}
                                                {renderFieldGroup(item, "contactInfo", "Կոնտակտային տվյալներ")}
                                                {renderFieldGroup(item, "courseInfo", "Դասընթացի տվյալներ")}
                                                {renderFieldGroup(item, "systemInfo", "Համակարգային տվյալներ")}
                                                {item.id && imageSrcs[item.id] && (
                                                    <div className="benefit-image">
                                                        <p>
                                                            <strong>Քարտի նախադիտում</strong>
                                                        </p>
                                                        <img
                                                            src={imageSrcs[item.id]}
                                                            alt="Benefit Card"
                                                        />
                                                    </div>
                                                )}

                                                <div className="btn_r_s">
                                                    <button
                                                        className="approve-btn"
                                                        onClick={() => handleAction(item.id, "approve")}
                                                    >
                                                        Հաստատել
                                                    </button>
                                                    <button
                                                        className="reject-btn"
                                                        onClick={() => handleAction(item.id, "reject")}
                                                    >
                                                        Մերժել
                                                    </button>
                                                    {item.pdf_path && (
                                                        <button
                                                            className="download-card-btn"
                                                            onClick={() => handleDownloadPDF(item.id)}
                                                        >
                                                            Ներբեռնել PDF
                                                        </button>
                                                    )}

                                                    <button
                                                        className="delete-btn"
                                                        onClick={() => handleDelete(item.id)}
                                                    >
                                                        Ջնջել
                                                    </button>

                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="loading-message">
                        Չկան "pending" կարգավիճակով օգտատերեր։
                    </div>
                )}
            </div>
        </div>
    );
};

export default PendingList;
